/** Client-side JWT session helpers (localStorage `auth_token`). */

const AUTH_STORAGE_KEYS = new Set(["auth_token", "user"]);

export function hasClientAuthToken(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const token = localStorage.getItem("auth_token");
    return typeof token === "string" && token.trim().length > 0;
  } catch {
    return false;
  }
}

function decodeJwtExpSeconds(token: string): number | null {
  try {
    const part = token.trim().split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64)) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when `auth_token` is a JWT with `exp` in the past. Non-JWT tokens are not treated as expired here. */
export function isClientAuthTokenExpired(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const token = localStorage.getItem("auth_token")?.trim();
    if (!token) return false;
    const exp = decodeJwtExpSeconds(token);
    if (exp == null) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return false;
  }
}

/**
 * Bearer for integrations OAuth — skip expired JWT so `/api/integrations/google/connect`
 * can fall back to `INTEGRATIONS_API_SECRET` (LINE deep links / sheets reconnect).
 */
export function readClientAuthTokenForIntegrationsConnect(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const token = localStorage.getItem("auth_token")?.trim();
    if (!token) return null;
    // Integrations endpoints expect a real JWT. If it's not a JWT (or missing `exp`),
    // treat as unusable so the server proxy can fall back to `INTEGRATIONS_API_SECRET`.
    if (token.split(".").length !== 3) return null;
    const exp = decodeJwtExpSeconds(token);
    if (exp == null) return null;
    if (Date.now() >= exp * 1000) return null;
    return token;
  } catch {
    return null;
  }
}

/**
 * Drop orphaned `user` when token is missing, or clear both when JWT `exp` passed.
 * Returns true if client auth storage was changed (caller should broadcast `auth:logout`).
 */
export function reconcileClientAuthSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    let changed = false;

    if (isClientAuthTokenExpired()) {
      if (localStorage.getItem("auth_token")) {
        localStorage.removeItem("auth_token");
        changed = true;
      }
      if (localStorage.getItem("user")) {
        localStorage.removeItem("user");
        changed = true;
      }
      return changed;
    }

    if (!hasClientAuthToken() && localStorage.getItem("user")) {
      localStorage.removeItem("user");
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

function isAuthStorageEvent(e: StorageEvent): boolean {
  return e.key === null || (e.key !== null && AUTH_STORAGE_KEYS.has(e.key));
}

/** Mirror localStorage JWT into httpOnly cookie for same-origin image proxies. */
export function syncClientAuthCookie(): void {
  if (typeof window === "undefined") return;

  const token = localStorage.getItem("auth_token")?.trim();
  if (!token) return;

  void fetch("/api/auth/set-token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  }).catch(() => {
    // Non-fatal — WorkspaceImage also sends Authorization directly.
  });
}

/**
 * When another tab clears or changes auth in localStorage, the `storage` event
 * does not fire in that tab and `auth:logout` does not cross tabs. Re-broadcast
 * as custom events so existing listeners stay in sync.
 */
export function installAuthSessionBridge(): () => void {
  if (typeof window === "undefined") return () => {};

  const notifyAuthChange = () => {
    if (reconcileClientAuthSession()) {
      window.dispatchEvent(new Event("auth:logout"));
      return;
    }
    if (!hasClientAuthToken()) {
      window.dispatchEvent(new Event("auth:logout"));
    }
  };

  notifyAuthChange();
  syncClientAuthCookie();

  const onAuthLogin = () => syncClientAuthCookie();

  const onStorage = (e: StorageEvent) => {
    if (!isAuthStorageEvent(e)) return;
    if (reconcileClientAuthSession()) {
      window.dispatchEvent(new Event("auth:logout"));
      return;
    }
    if (!hasClientAuthToken()) {
      window.dispatchEvent(new Event("auth:logout"));
    } else if (e.key === "auth_token" && e.newValue) {
      window.dispatchEvent(new Event("auth:login"));
      window.dispatchEvent(new Event("auth:loginSuccess"));
    }
  };

  window.addEventListener("storage", onStorage);
  window.addEventListener("auth:login", onAuthLogin);
  window.addEventListener("auth:loginSuccess", onAuthLogin);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("auth:login", onAuthLogin);
    window.removeEventListener("auth:loginSuccess", onAuthLogin);
  };
}

/** Subscribe to auth changes in this tab and from other tabs. */
export function subscribeAuthSession(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  const onStorage = (e: StorageEvent) => {
    if (isAuthStorageEvent(e)) onChange();
  };

  window.addEventListener("auth:logout", onChange);
  window.addEventListener("auth:login", onChange);
  window.addEventListener("auth:loginSuccess", onChange);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener("auth:logout", onChange);
    window.removeEventListener("auth:login", onChange);
    window.removeEventListener("auth:loginSuccess", onChange);
    window.removeEventListener("storage", onStorage);
  };
}
